import * as React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { RenderModeServiceV1 } from '@oneaudi/render-mode-service';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import type { ContentServiceV1 } from '@oneaudi/content-service';
import { ContentContextProvider } from '@oneaudi/feature-app-utils';
import type { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import type { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ButtonsContentLoader } from './components/Buttons';
import type { ButtonsContent } from '../types';
import type { FalconContent } from './utils/normalize-falcon-data';
import { mapContent } from './utils/normalize-falcon-data';
import { TrackingContext } from './context';
import { getDesignLibrary } from '../alpha-helper';
interface FeatureServiceDependencies extends FeatureServices {
  readonly 'audi-content-service': ContentServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
  readonly 's2:async-ssr-manager'?: AsyncSsrManagerV1;
  readonly 's2:serialized-state-manager'?: SerializedStateManagerV1;
  readonly 'audi-render-mode-service'?: RenderModeServiceV1;
}

export const getAsyncState = async (contentService: ContentServiceV1): Promise<ButtonsContent> => {
  const contentServiceData: ButtonsContent | FalconContent | undefined =
    await contentService?.getContent();
  return mapContent(contentServiceData);
};

export default {
  dependencies: {
    featureServices: { 'audi-content-service': '^1.0.0', 'audi-render-mode-service': '^1.0.0' },
  },
  externals: {
    react: '*',
    'styled-components': '*',
    ...getDesignLibrary({ unifiedWebCommonVersion: '^1.13.0' }),
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },
  create: ({
    featureServices: {
      'audi-content-service': contentService,
      's2:async-ssr-manager': asyncSSRManager,
      's2:serialized-state-manager': serializedStateManager,
      'audi-render-mode-service': renderModeService,
      'audi-tracking-service': trackingService,
    },
    config,
  }: FeatureAppEnvironment<FeatureServiceDependencies, ButtonsContent>) => {
    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }
    let asyncState: ButtonsContent;
    if (asyncSSRManager) {
      asyncSSRManager.scheduleRerender(
        (async () => {
          asyncState = await getAsyncState(contentService);
          serializedStateManager?.register(() => JSON.stringify(asyncState));
        })(),
      );
    } else {
      const serializedState = serializedStateManager?.getSerializedState();

      if (serializedState) {
        asyncState = JSON.parse(serializedState);
      } else {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        (async () => {
          asyncState = await getAsyncState(contentService);
        })();
      }
    }

    return {
      render: () => (
        <ContentContextProvider contentService={contentService}>
          {/* @ts-expect-error optional dependency */}
          <TrackingContext.Provider value={trackingService}>
            <UniversalEditorProvider
              contentService={contentService}
              renderModeService={renderModeService}
            >
              <ButtonsContentLoader config={config} ssrContent={asyncState} />
            </UniversalEditorProvider>
          </TrackingContext.Provider>
        </ContentContextProvider>
      ),
    };
  },
} as FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies>;
